import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Image as ImageIcon, Download, Check, X, Paperclip } from 'lucide-react';

const APP_STATUS_STYLES = {
  submitted: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const AdminMarketplaceProject = () => {
  const { id } = useParams();
  const { getAuthHeaders, API_URL } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reasons, setReasons] = useState({});
  const [replies, setReplies] = useState({});

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/marketplace/projects/${id}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (error) {
      console.error('Failed to load project:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await axios.get(`${API_URL}/api/marketplace/files/${file.file_id}`, {
        headers: getAuthHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || 'download';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const decide = async (appId, decision) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/marketplace/projects/${id}/applications/${appId}/decide`,
        { decision, reason: reasons[appId] || '' },
        { headers: getAuthHeaders() },
      );
      toast.success(`Application ${decision}`);
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    }
  };

  const changeStatus = async (status) => {
    try {
      const fd = new FormData();
      fd.append('status', status);
      await axios.put(`${API_URL}/api/admin/marketplace/projects/${id}`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Status updated');
      fetchProject();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const postReply = async (parentId) => {
    const body = replies[parentId];
    if (!body || !body.trim()) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/marketplace/projects/${id}/comments`,
        { body, parent_id: parentId },
        { headers: getAuthHeaders() },
      );
      setReplies((prev) => ({ ...prev, [parentId]: '' }));
      toast.success('Reply posted');
      fetchProject();
    } catch (error) {
      toast.error('Failed to post reply');
    }
  };

  if (loading) return <DashboardLayout><div className="text-center py-12 text-[#64748B]">Loading...</div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="text-center py-12 text-[#64748B]">Project not found.</div></DashboardLayout>;

  const { project, applications, comments } = data;
  const questions = comments.filter((c) => c.author_role === 'partner');
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_id) { (acc[c.parent_id] = acc[c.parent_id] || []).push(c); }
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        <button onClick={() => navigate('/admin/marketplace')} className="flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#1A1A1A]">
          <ArrowLeft className="w-4 h-4" /> Back to marketplace
        </button>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1A1A]">{project.title}</h2>
            {project.customer && <p className="text-[#64748B] text-sm mt-1">{project.customer}{project.vertical ? ` · ${project.vertical}` : ''}</p>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#64748B]">Status</span>
            <Select value={project.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-48 capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="applications_closed">Applications Closed</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Brief summary + downloads */}
        <Card>
          <CardHeader><CardTitle className="text-base">Project details</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-[#475569]">
            {project.description && <p className="whitespace-pre-wrap">{project.description}</p>}
            <div className="grid sm:grid-cols-2 gap-3">
              {project.scope && <div><b className="text-[#1A1A1A]">Scope:</b> {project.scope}</div>}
              {project.timeline && <div><b className="text-[#1A1A1A]">Timeline:</b> {project.timeline}</div>}
              {project.deliverables && <div><b className="text-[#1A1A1A]">Deliverables:</b> {project.deliverables}</div>}
              {project.budget && <div><b className="text-[#1A1A1A]">Budget:</b> {project.budget}</div>}
            </div>
            {(project.skills || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">{project.skills.map((s) => <Badge key={s} variant="secondary" className="font-normal">{s}</Badge>)}</div>
            )}
            {(project.brief_file || project.blueprint_file) && (
              <div className="flex flex-wrap gap-2 pt-1">
                {project.brief_file && <Button variant="outline" size="sm" onClick={() => downloadFile(project.brief_file)}><FileText className="w-4 h-4 mr-1.5" /> Brief <Download className="w-3.5 h-3.5 ml-1.5" /></Button>}
                {project.blueprint_file && <Button variant="outline" size="sm" onClick={() => downloadFile(project.blueprint_file)}><ImageIcon className="w-4 h-4 mr-1.5" /> Blueprint <Download className="w-3.5 h-3.5 ml-1.5" /></Button>}
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs defaultValue="applications">
          <TabsList>
            <TabsTrigger value="applications">Applications ({applications.length})</TabsTrigger>
            <TabsTrigger value="qa">Q&amp;A ({questions.length})</TabsTrigger>
          </TabsList>

          {/* Applications */}
          <TabsContent value="applications" className="space-y-4 mt-4">
            {applications.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-[#64748B]">No applications yet.</CardContent></Card>
            ) : (
              applications.map((a) => (
                <Card key={a.id}>
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-[#1A1A1A]">{a.partner_name}</p>
                        <p className="text-xs text-[#64748B]">{a.partner_email} · applied {a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</p>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${APP_STATUS_STYLES[a.status]}`}>{a.status}</span>
                    </div>
                    {a.cover_note && <Field label="Cover note" value={a.cover_note} />}
                    {a.relevant_experience && <Field label="Relevant experience" value={a.relevant_experience} />}
                    {a.proposed_timeline && <Field label="Proposed timeline" value={a.proposed_timeline} />}
                    {a.attachment && (
                      <Button variant="outline" size="sm" onClick={() => downloadFile(a.attachment)}>
                        <Paperclip className="w-4 h-4 mr-1.5" /> {a.attachment.filename} <Download className="w-3.5 h-3.5 ml-1.5" />
                      </Button>
                    )}
                    {a.status === 'submitted' && (
                      <div className="space-y-2 pt-2 border-t border-[#F0F0EE]">
                        <Input placeholder="Optional reason / note for decision"
                          value={reasons[a.id] || ''} onChange={(e) => setReasons((p) => ({ ...p, [a.id]: e.target.value }))} />
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => decide(a.id, 'approved')}>
                            <Check className="w-4 h-4 mr-1.5" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => decide(a.id, 'rejected')}>
                            <X className="w-4 h-4 mr-1.5" /> Reject
                          </Button>
                        </div>
                      </div>
                    )}
                    {a.status !== 'submitted' && a.decision_reason && (
                      <p className="text-xs text-[#64748B] italic pt-1 border-t border-[#F0F0EE]">{a.decision_reason}</p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Q&A */}
          <TabsContent value="qa" className="space-y-4 mt-4">
            {questions.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-[#64748B]">No questions yet.</CardContent></Card>
            ) : (
              questions.map((q) => (
                <Card key={q.id}>
                  <CardContent className="p-5 space-y-3">
                    <div>
                      <p className="text-xs font-medium text-[#64748B]">{q.author_name} · {q.created_at ? new Date(q.created_at).toLocaleString() : ''}</p>
                      <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap mt-0.5">{q.body}</p>
                    </div>
                    {(repliesByParent[q.id] || []).map((r) => (
                      <div key={r.id} className="ml-4 pl-3 border-l-2 border-[#ef785b]/30">
                        <p className="text-xs font-medium text-[#ef785b]">Expedify Team · {r.created_at ? new Date(r.created_at).toLocaleString() : ''}</p>
                        <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap mt-0.5">{r.body}</p>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-1">
                      <Textarea rows={1} placeholder="Reply to this question..."
                        value={replies[q.id] || ''} onChange={(e) => setReplies((p) => ({ ...p, [q.id]: e.target.value }))} />
                      <Button variant="outline" onClick={() => postReply(q.id)}>Reply</Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-[#64748B]">{label}</p>
    <p className="text-sm text-[#475569] whitespace-pre-wrap">{value}</p>
  </div>
);

export default AdminMarketplaceProject;
